import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Empty, ErrorNote, Field, Modal, PageHead, Spinner, Status, Table } from '../../components/ui'
import { api, wsPath } from '../../lib/api'
import { fmtDate, fmtMoney, fmtNum } from '../../lib/format'
import { useT } from '../../lib/i18n'
import { useGroups } from './Groups'
import { useRole, useWs } from './useWs'

interface Row {
  enrollment_id: string
  partner_id: string
  status: string
  name: string
  email: string
  country: string | null
  website: string | null
  created_at: string
  clicks: number
  sales: number
  revenue: string
  earnings: string
  balance: string
  payout_method: string | null
  application: { message?: string | null; answers?: Record<string, string> } | null
  group_id: string | null
  group_name: string | null
}

export function PartnersPage() {
  const { t, locale } = useT()
  const ws = useWs()
  const role = useRole()
  const qc = useQueryClient()
  const [filter, setFilter] = useState('')
  const [invite, setInvite] = useState(false)
  const q = useQuery({
    queryKey: ['partners', ws, filter],
    queryFn: () => api<{ partners: Row[] }>(wsPath(ws, `/partners${filter ? `?status=${filter}` : ''}`)),
  })
  const prog = useQuery({
    queryKey: ['program', ws],
    queryFn: () => api<{ program: { currency: string; slug: string } }>(wsPath(ws, '/program')),
  })
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: string }) =>
      api(wsPath(ws, `/partners/${id}/decision`), { body: { decision } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partners', ws] }),
  })
  const groups = useGroups(ws)
  const move = useMutation({
    mutationFn: ({ id, groupId }: { id: string; groupId: string | null }) =>
      api(wsPath(ws, `/partners/${id}/group`), { body: { groupId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners', ws] })
      qc.invalidateQueries({ queryKey: ['groups', ws] })
    },
  })
  const groupList = groups.data?.groups ?? []
  const cur = prog.data?.program.currency ?? 'USD'
  const canManage = role === 'owner' || role === 'admin'
  const rows = q.data?.partners ?? []
  const epc = (r: Row) => (r.clicks ? fmtMoney(Math.round(Number(r.earnings) / r.clicks), cur, locale) : '—')
  return (
    <div>
      <PageHead
        title={t('partners')}
        actions={
          canManage && (
            <button type="button" className="btn btn-primary" onClick={() => setInvite(true)}>
              {t('invite')}
            </button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap gap-1">
        {['', 'pending', 'approved', 'rejected', 'blocked'].map((s) => (
          <button
            key={s}
            type="button"
            className={`tab ${filter === s ? 'on' : ''}`}
            onClick={() => setFilter(s)}
          >
            {s ? <Status value={s} kind="enrollment" /> : t('all')}
          </button>
        ))}
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : !rows.length ? (
        <Empty
          title={t('emptyPartners')}
          text={t('emptyPartnersText')}
          action={
            prog.data && (
              <div className="flex gap-2">
                <a className="btn" href={`/join/${prog.data.program.slug}`} target="_blank" rel="noreferrer">
                  {t('programPage')} ↗
                </a>
                {canManage && (
                  <button type="button" className="btn btn-primary" onClick={() => setInvite(true)}>
                    {t('invite')}
                  </button>
                )}
              </div>
            )
          }
        />
      ) : (
        <Table
          head={[
            t('partner'),
            t('status'),
            t('group'),
            t('clicks'),
            t('sales'),
            t('revenue'),
            'EPC',
            t('balance'),
            t('method'),
            '',
          ]}
        >
          {rows.map((r) => (
            <tr key={r.enrollment_id}>
              <td>
                <div className="text-ink">{r.name}</div>
                <div className="text-xs text-mute">
                  {r.email}
                  {r.country ? ` · ${r.country}` : ''}
                </div>
                {r.status === 'pending' && r.application?.message && (
                  <div className="mt-1 max-w-xs truncate text-xs text-body" title={r.application.message}>
                    “{r.application.message}”
                  </div>
                )}
              </td>
              <td>
                <Status value={r.status} kind="enrollment" />
                <div className="mono mt-1 text-[11px] text-mute">{fmtDate(r.created_at, locale)}</div>
              </td>
              <td>
                {canManage && groupList.length ? (
                  <select
                    className="select select-sm"
                    aria-label={t('moveToGroup')}
                    value={r.group_id ?? ''}
                    disabled={move.isPending}
                    onChange={(e) => move.mutate({ id: r.enrollment_id, groupId: e.target.value || null })}
                  >
                    <option value="">{t('defaultGroup').split(' · ')[0]}</option>
                    {groupList.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-xs">{r.group_name ?? '—'}</span>
                )}
                {r.status === 'pending' && r.application?.answers && (
                  <div className="mt-1 max-w-xs text-xs text-body">
                    {Object.values(r.application.answers).map((a) => (
                      <div key={a} className="truncate" title={a}>
                        “{a}”
                      </div>
                    ))}
                  </div>
                )}
              </td>
              <td className="mono">{fmtNum(r.clicks, locale)}</td>
              <td className="mono">{fmtNum(r.sales, locale)}</td>
              <td className="mono">{fmtMoney(r.revenue, cur, locale)}</td>
              <td className="mono">{epc(r)}</td>
              <td className={`mono ${Number(r.balance) < 0 ? 'text-danger' : ''}`}>
                {fmtMoney(r.balance, cur, locale)}
              </td>
              <td className="text-xs">{r.payout_method ?? '—'}</td>
              <td className="text-right">
                {canManage && r.status === 'pending' && (
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => decide.mutate({ id: r.enrollment_id, decision: 'approve' })}
                    >
                      {t('approve')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => decide.mutate({ id: r.enrollment_id, decision: 'reject' })}
                    >
                      {t('reject')}
                    </button>
                  </div>
                )}
                {canManage && r.status === 'approved' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => decide.mutate({ id: r.enrollment_id, decision: 'block' })}
                  >
                    {t('block')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <InviteModal open={invite} onClose={() => setInvite(false)} />
    </div>
  )
}

function InviteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const m = useMutation({
    mutationFn: () => api(wsPath(ws, '/partners/invite'), { body: { email, ...(name ? { name } : {}) } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners', ws] })
      setEmail('')
      setName('')
      onClose()
    },
  })
  return (
    <Modal open={open} onClose={onClose} title={t('invite')}>
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          m.mutate()
        }}
      >
        <p className="text-sm text-body">{t('invitePartnerText')}</p>
        <Field label={t('email')}>
          <input
            className="input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label={t('name')}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={m.isPending}>
            {t('send')}
          </button>
        </div>
        <ErrorNote error={m.error} />
      </form>
    </Modal>
  )
}
